// Telegram WebApp SDK adapter. Reads from `window.Telegram.WebApp` set by telegram-web-app.js.
// We don't pull in a heavy SDK package — this is the minimal surface we need.

interface TGUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
}

interface TGWebApp {
  initData: string
  initDataUnsafe: {
    user?: TGUser
    start_param?: string
  }
  colorScheme: 'light' | 'dark'
  platform?: string
  themeParams: Record<string, string>
  viewportHeight: number
  ready: () => void
  expand: () => void
  close: () => void
  disableVerticalSwipes?: () => void
  requestFullscreen?: () => void
  onEvent?: (event: string, cb: () => void) => void
  openTelegramLink?: (url: string) => void
  openLink?: (url: string, opts?: { try_instant_view?: boolean }) => void
  shareToStory?: (url: string) => void
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void
    selectionChanged: () => void
  }
  MainButton: {
    text: string
    color: string
    textColor: string
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
    setText: (t: string) => void
    enable: () => void
    disable: () => void
  }
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
  openInvoice?: (url: string, cb?: (status: string) => void) => void
  showPopup?: (params: { title?: string; message: string; buttons?: { id?: string; type?: string; text?: string }[] }, cb?: (id: string) => void) => void
}

declare global {
  interface Window {
    Telegram?: { WebApp: TGWebApp }
  }
}

export function tg(): TGWebApp | null {
  return window.Telegram?.WebApp ?? null
}

export function initTelegram() {
  const wa = tg()
  if (!wa) {
    applyTheme()  // dev / outside Telegram
    return
  }
  wa.ready()
  wa.expand()
  wa.disableVerticalSwipes?.()
  applyTheme()
  // follow Telegram theme changes when in auto mode
  wa.onEvent?.('themeChanged', () => { if (getThemeMode() === 'auto') applyTheme() })
}

// ---------------- theme (light / dark / auto) ----------------

export type ThemeMode = 'auto' | 'light' | 'dark'

export function getThemeMode(): ThemeMode {
  const v = (typeof localStorage !== 'undefined' && localStorage.getItem('theme')) as ThemeMode | null
  // Brand is dark-first: unset → dark. 'auto' still follows Telegram if chosen.
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'dark'
}

export function resolveDark(mode: ThemeMode = getThemeMode()): boolean {
  if (mode === 'dark') return true
  if (mode === 'light') return false
  const wa = tg()
  if (wa?.colorScheme) return wa.colorScheme === 'dark'
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

export function applyTheme(mode: ThemeMode = getThemeMode()): void {
  const dark = resolveDark(mode)
  document.documentElement.classList.toggle('dark', dark)
  const bg = dark ? '#0a0a0b' : '#f4efe9'
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg)
  const wa = tg()
  try { wa?.setHeaderColor(bg); wa?.setBackgroundColor(bg) } catch { /* noop */ }
}

export function setThemeMode(mode: ThemeMode): void {
  try { localStorage.setItem('theme', mode) } catch { /* noop */ }
  applyTheme(mode)
}

export function tgBack(cb: () => void) {
  const wa = tg()
  if (!wa) return () => {}
  wa.BackButton.show()
  wa.BackButton.onClick(cb)
  return () => {
    wa.BackButton.offClick(cb)
    wa.BackButton.hide()
  }
}

export function openTgLink(url: string) {
  const wa = tg()
  if (wa?.openTelegramLink && url.startsWith('https://t.me/')) {
    wa.openTelegramLink(url)
  } else {
    wa?.openLink?.(url) ?? window.open(url, '_blank')
  }
}

/** Open an external http(s) payment page (Platega). */
export function openExternal(url: string) {
  const wa = tg()
  if (wa?.openLink) wa.openLink(url)
  else window.open(url, '_blank')
}

/** Open a Telegram Stars invoice link; resolves with the final status. */
export function openInvoice(url: string): Promise<string> {
  return new Promise(resolve => {
    const wa = tg()
    if (wa?.openInvoice) wa.openInvoice(url, status => resolve(status))
    else {
      window.open(url, '_blank')
      resolve('unknown')
    }
  })
}

/** Open a custom-scheme deep link (e.g. happ://). */
export function openDeepLink(url: string) {
  try {
    window.location.href = url
  } catch {
    window.open(url, '_blank')
  }
}

// The connect bridge (redirects a custom scheme that Telegram's WebView won't hand
// to the OS) is a static page bundled WITH the app, served from the app's own clean
// origin (Cloudflare Pages). Hosting it here — not on the backend domain — avoids the
// Safe-Browsing "deceptive site" warning that flags the backend domain.
function connectBridge(query: string): string {
  // Cloudflare Pages serves connect.html at the extensionless /connect (no 308 hop).
  return `${window.location.origin}${import.meta.env.BASE_URL}connect?${query}`
}

/**
 * Open the Happ app for a subscription. Telegram's in-app WebView does NOT
 * hand custom schemes (happ://) to the OS, so we open an intermediate HTTPS
 * "connect" page via tg.openLink — Telegram's in-app browser DOES route the
 * happ:// link from there. Falls back to a direct deep link outside Telegram.
 */
export function openHapp(happLink: string, subLink?: string) {
  if (!happLink) return
  const wa = tg()
  if (wa?.openLink) {
    wa.openLink(connectBridge(
      `happ=${encodeURIComponent(happLink)}` +
      (subLink ? `&sub=${encodeURIComponent(subLink)}` : '')))
  } else {
    openDeepLink(happLink)
  }
}

/**
 * Open Koala Clash (Windows) for a subscription. Same constraint as Happ: Telegram's
 * WebView won't hand a clash:// scheme to the OS, so route through the HTTPS /api/connect
 * bridge page which redirects to the deep link. Direct deep link outside Telegram.
 */
export function openKoala(clashLink: string, subLink?: string) {
  if (!clashLink) return
  const wa = tg()
  if (wa?.openLink) {
    wa.openLink(connectBridge(
      `client=koala&link=${encodeURIComponent(clashLink)}` +
      (subLink ? `&sub=${encodeURIComponent(subLink)}` : '')))
  } else {
    openDeepLink(clashLink)
  }
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light') {
  tg()?.HapticFeedback.impactOccurred(style)
}

export function selectionHaptic() {
  tg()?.HapticFeedback.selectionChanged()
}

export function getInitData(): string {
  return tg()?.initData ?? ''
}

/** Label + icon for the device the mini-app is opened on (Telegram WebApp platform). */
export function getPlatform(): { label: string; icon: 'device-mobile' | 'device-desktop' | 'device-laptop' } {
  const p = (tg()?.platform ?? '').toLowerCase()
  if (p === 'ios') return { label: 'Этот iPhone', icon: 'device-mobile' }
  if (p === 'android') return { label: 'Этот Android', icon: 'device-mobile' }
  if (p === 'macos') return { label: 'Этот Mac', icon: 'device-desktop' }
  if (p === 'tdesktop' || p === 'weba' || p === 'webk') return { label: 'Это устройство', icon: 'device-laptop' }
  return { label: 'Это устройство', icon: 'device-mobile' }
}

export function getUser(): TGUser | null {
  return tg()?.initDataUnsafe.user ?? null
}

// Mock user when running outside Telegram (local dev)
export function getUserOrMock(): TGUser {
  return (
    getUser() ?? {
      id: 5463598075,
      first_name: 'Иван',
      last_name: '',
      username: 'admin',
    }
  )
}
